/* eslint-disable jsx-a11y/tabindex-no-positive */
/* eslint-disable react/button-has-type */
import * as React from 'react';

import { Checkbox } from '@lightspeed/flame/Checkbox';
import { FlameTheme, Flex, Box } from '@lightspeed/flame/Core';
import some from 'lodash/some';

import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import Localize from 'app/src/localize/Localize';
import { translate } from 'pages/utils/translate';

interface IRefunds {
  Payment: {
    amount: string;
    tipAmount: string;
  };
}

export interface ICreditDebitPayments {
  model: {
    createTime: string;
    salePaymentID: number;
    amount: string;
    CCCharge?: {
      xnum: string;
    };
    tipAmount: Decimal;
    paymentID: string;
  };
  serviceModel?: {
    Refunds: IRefunds | [IRefunds];
    refundedAmount?: string;
  };
  details: {
    cardBrand: string;
    cardType: string;
    maskedDigits: string;
    isDebit: boolean;
    isInterac: boolean;
    isBancontact: boolean;
    isIdeal: boolean;
    transactionDate: string;
  };
  maximumRefundAmount: Decimal;
}
export interface IViewData {
  outstandingBalance: Decimal;
  payment: ICreditDebitPayments;
}

export const PaymentCreditDebitRefundByRefView = ({
  viewData,
  onClickTipRefundButton,
}: {
  viewData: IViewData;
  onClickTipRefundButton: ({ refundTip }: { refundTip: boolean }) => void;
}) => {
  const [tipRefundState, setTipRefundState] = React.useState(false);

  const isServicePayment = (payment: ICreditDebitPayments) => {
    return payment?.model?.paymentID && payment?.model?.paymentID !== '';
  };

  const hasTipRefunded = (payment: ICreditDebitPayments) => {
    const refunds = Array.isArray(payment?.serviceModel?.Refunds)
      ? payment?.serviceModel?.Refunds
      : [payment?.serviceModel?.Refunds];

    return some(
      refunds,
      refund => refund && Format.parseDecimal(refund.Payment.tipAmount, new Decimal(0)) < 0
    );
  };

  const cardInformation = (payment: ICreditDebitPayments) => {
    let cardInformation = '';

    if (payment.details) {
      const cardType = payment.details.isDebit
        ? Localize.translate('Debit')
        : Localize.translate('Credit Card');

      cardInformation = `${cardType} ${payment.details.maskedDigits}`;
    } else if (payment.model.CCCharge) {
      cardInformation = `${payment.model.CCCharge.xnum}`;
    }

    return cardInformation;
  };

  const transactionDate = (payment: ICreditDebitPayments) => {
    let transactionDate = '';

    if (payment.details) {
      transactionDate = payment.details.transactionDate;
    } else if (payment.model.CCCharge) {
      transactionDate = payment.model.createTime.split('T')[0];
    }

    return transactionDate;
  };

  const handleTipSelection = (event: React.ChangeEvent<any>) => {
    const newTipRefundState = event.target.checked;
    setTipRefundState(newTipRefundState);
    onClickTipRefundButton({ refundTip: newTipRefundState });
  };

  const payment: ICreditDebitPayments = viewData.payment;
  const tipAmount = new Decimal(payment?.model?.tipAmount) || new Decimal(0);

  return (
    <FlameTheme>
      <table className="refunds_list" cellPadding="0" cellSpacing="0">
        <tbody>
          <tr>
            <th colSpan={2}>{translate('Enter Amount To Refund')}</th>
          </tr>
          <tr>
            <th>{translate('Card')}</th>
            <td className="notranslate" aria-label="cardInformation">
              {cardInformation(payment)}
            </td>
          </tr>
          <tr>
            <th>{translate('Date')}</th>
            <td className="notranslate" aria-label="transaction date">
              {transactionDate(payment)}
            </td>
          </tr>
          <tr>
            <th>{translate('Original charge remaining')}</th>
            <td className="notranslate" aria-label="maximum refund available">
              <span>{Format.formatMoney(payment.maximumRefundAmount, 0)}</span>
              {tipAmount.isGreaterThan(0) && hasTipRefunded(payment) === false && (
                <span>{` + ${Format.formatMoney(tipAmount, 0)} tip`}</span>
              )}
            </td>
          </tr>
          <tr id="original_charge_message" className="hidden">
            <th colSpan={2}>
              <p>
                {translate('Original charge has not settled.')}
                <br />
                {translate('You can only refund the original amount.')}
              </p>
            </th>
          </tr>
          {tipAmount.isGreaterThan(0) && hasTipRefunded(payment) === false && (
            <tr>
              <th>{translate('Include original tip')}</th>
              <th>
                <Checkbox
                  disabled={hasTipRefunded(payment)}
                  checked={tipRefundState}
                  aria-label="tip selection checkbox"
                  onChange={handleTipSelection}
                  label={`${Format.formatMoney(tipAmount, 0)}`}
                />
              </th>
            </tr>
          )}
          <tr>
            <th>{translate('Refund')}</th>
            <td>
              <input
                disabled
                id="credit_card_amount"
                tabIndex={20}
                type="text"
                size={10}
                className="money"
                aria-label="refund amount input"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <Flex p="2" justifyContent="flex-end">
        <Box mr="1">
          <button
            id="add_refund"
            tabIndex={22}
            style={{ fontWeight: 'bold' }}
            className="gui-def-button"
            aria-label="refund button"
          >
            {isServicePayment(payment) ? translate('Refund') : translate('Add Refund')}
          </button>
        </Box>
        <Box>
          <button
            id="refund_cancel"
            tabIndex={24}
            className="gui-def-button"
            aria-label="refund cancel"
          >
            {translate('Cancel')}
          </button>
        </Box>
      </Flex>
    </FlameTheme>
  );
};
